import React from 'react';

import { Box, Typography } from '@mui/material';

import { HelpTypes, TypeOfUser, userActions } from '@pharmaplan/common';
import { ReactComponent as Logo } from '@pharmaplan/common/assets/icons/bigLogo.svg';

import usePharmaPlanConsent from '../../hooks/CrossPlatform/usePharmaPlanConsent';
import { SkeletonVariants } from '../../helpers/Constants';
import strings from '../../localization';
import CustomButton from '../common/CustomButton';
import LoginCustomCard from '../common/LoginCustomCard';
import WithSkeleton from '../common/WithSkeleton';

import classes from './styles';

const SkeletonTypography = WithSkeleton({
  successAction: userActions.getPharmaPlanInvitationPreview,
  variant: SkeletonVariants.text,
  width: 160,
})(Typography);

const PharmaPlanConsent = () => {
  const {
    preview,
    isConfirmed,
    isInvalid,
    handleConfirm,
    handleNotNow,
  } = usePharmaPlanConsent();

  const userTypeLabels: Partial<Record<TypeOfUser | HelpTypes, string>> = {
    [TypeOfUser.pharmacy]: strings.pharmacy,
    [TypeOfUser.pharmacist]: strings.pharmacist,
    [TypeOfUser.technician]: strings.technician,
    [TypeOfUser.student]: strings.student,
    [HelpTypes.pharmacy]: strings.pharmacy,
    [HelpTypes.pharmacist]: strings.pharmacist,
    [HelpTypes.technicians]: strings.technician,
    [HelpTypes.students]: strings.student,
  };
  const userTypeLabel = preview
    ? userTypeLabels[preview.userType] ?? strings.professional
    : '';

  const textArray = [
    { key: 1, text: strings.joinPharmaPlan, sx: classes.title },
    isInvalid && {
      key: 2,
      text: strings.invalidPharmaPlanInvitation,
      sx: classes.description,
    },
  ].filter(Boolean) as { key: number; text: string; sx: Record<string, unknown> }[];

  const details = [
    { label: strings.name, value: preview?.userName ?? '' },
    { label: strings.email, value: preview?.email ?? '' },
  ];
  const consentPrompt = strings.formatString(
    strings.pharmaPlanConsentPrompt,
    userTypeLabel,
  );

  const renderContent = () => {
    if (preview?.linkExpired) {
      return (
        <Typography sx={classes.successMessage}>
          {strings.portingLinkExpired}
        </Typography>
      );
    }

    if (isInvalid) {
      return (
        <Box sx={classes.actions}>
          <CustomButton
            label={strings.backToLogin}
            onClick={handleNotNow}
          />
        </Box>
      );
    }

    if (isConfirmed) {
      return (
        <Typography sx={classes.successMessage}>
          {strings.pharmaPlanConsentSuccess}
        </Typography>
      );
    }

    return (
      <Box>
        <SkeletonTypography sx={classes.consentPrompt}>
          {consentPrompt}
        </SkeletonTypography>
        <Box sx={classes.detailsContainer}>
          {details.map(({ label, value }) =>
            (
              <Box key={label} sx={classes.detailRow}>
                <Typography sx={classes.detailLabel}>
                  {label}
                  :
                </Typography>
                <SkeletonTypography sx={classes.detailValue}>
                  {value}
                </SkeletonTypography>
              </Box>
            ))}
        </Box>

        <Typography sx={classes.note}>
          {strings.pharmaPlanSameCredentials}
        </Typography>

        <Box sx={classes.actions}>
          <CustomButton
            label={strings.notNow}
            onClick={handleNotNow}
            variant="text"
            customButtonStyle={[classes.button, classes.secondaryButton]}
          />
          <CustomButton
            label={strings.confirm}
            customButtonStyle={classes.button}
            onClick={handleConfirm}
          />
        </Box>
      </Box>
    );
  };

  return (
    <LoginCustomCard
      icon={<Logo />}
      textArray={textArray}
      customCard={classes.card}
    >
      {renderContent()}
    </LoginCustomCard>
  );
};

export default PharmaPlanConsent;
